import { useForm } from '@inertiajs/react'
import { Save, School } from 'lucide-react'
import type { FormEvent } from 'react'

import { AppShell } from '@/layouts/AppShell'
import { PageHeader } from '@/components/layout/PageHeader'
import { Card } from '@/components/ui/Card'
import { Select } from '@/components/ui/Select'
import { Button, buttonClasses } from '@/components/ui/Button'
import { FieldError } from '@/components/feedback/FieldError'
import type { Breadcrumb } from '@/types/shared'

type Period = {
  schoolYear: number
  term: number
  label: string
}

type Subject = {
  value: string
  label: string
}

type AcademicGrade = {
  schoolYear: number
  term: number
  subject: string
  rating: number | null
}

type GradeFormValue = Omit<AcademicGrade, 'rating'> & {
  rating: string
}

type AcademicGradesEditProps = {
  periods: Period[]
  subjects: Subject[]
  grades: AcademicGrade[]
  breadcrumbs: Breadcrumb[]
}

const ratingOptions = ['1', '2', '3', '4', '5']

export default function AcademicGradesEdit({
  periods,
  subjects,
  grades,
  breadcrumbs,
}: AcademicGradesEditProps) {
  const { data, setData, errors, processing, transform, patch } = useForm({
    grades: grades.map((grade) => ({
      schoolYear: grade.schoolYear,
      term: grade.term,
      subject: grade.subject,
      rating: grade.rating?.toString() ?? '',
    })),
  })

  const gradeByCell = new Map(
    data.grades.map((grade) => [cellKey(grade.schoolYear, grade.term, grade.subject), grade])
  )

  const updateRating = (period: Period, subject: Subject, rating: string) => {
    setData((previous) => ({
      grades: previous.grades.map((grade) =>
        grade.schoolYear === period.schoolYear && grade.term === period.term && grade.subject === subject.value
          ? { ...grade, rating }
          : grade
      ),
    }))
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    transform((current) => ({
      academic_grades: current.grades.map((grade: GradeFormValue) => ({
        school_year: grade.schoolYear,
        term: grade.term,
        subject: grade.subject,
        rating: grade.rating,
      })),
    }))

    patch('/player/academic_grades', {
      preserveScroll: true,
      onFinish: () => {
        transform((current) => current)
      },
    })
  }

  return (
    <AppShell>
      <PageHeader
        title="学業成績"
        description="1年1学期から3年2学期までの5段階評定を入力できます。未入力の科目は空欄のまま保存できます。"
        breadcrumbs={breadcrumbs}
        actions={
          <a href="/player" className={buttonClasses({ variant: 'ghost', size: 'sm' })}>
            ダッシュボードへ
          </a>
        }
      />

      <form onSubmit={submit} className="space-y-6">
        <Card className="space-y-6">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-500/20 text-brand-100">
                <School className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">評定入力</h2>
                <p className="text-sm text-white/60">国語・数学・英語・社会・理科の評定をまとめて保存します。</p>
              </div>
            </div>
            <Button type="submit" loading={processing}>
              <Save className="h-4 w-4" />
              保存する
            </Button>
          </div>

          <FieldError message={(errors as Record<string, string>).academic_grades} />

          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] table-fixed border-collapse text-sm">
              <thead>
                <tr>
                  <th className="w-32 border-b border-white/10 px-3 py-3 text-left font-medium text-white/60">
                    期間
                  </th>
                  {subjects.map((subject) => (
                    <th
                      key={subject.value}
                      className="border-b border-white/10 px-3 py-3 text-center font-medium text-white/60"
                    >
                      {subject.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {periods.map((period) => (
                  <tr key={`${period.schoolYear}-${period.term}`}>
                    <th className="border-b border-white/10 px-3 py-3 text-left font-medium text-white">
                      {period.label}
                    </th>
                    {subjects.map((subject) => {
                      const grade = gradeByCell.get(cellKey(period.schoolYear, period.term, subject.value))

                      return (
                        <td key={subject.value} className="border-b border-white/10 px-3 py-3">
                          <Select
                            aria-label={`${period.label} ${subject.label}`}
                            value={grade?.rating ?? ''}
                            onChange={(event) => updateRating(period, subject, event.target.value)}
                            className="mx-auto max-w-24 text-center"
                          >
                            <option value="">-</option>
                            {ratingOptions.map((rating) => (
                              <option key={rating} value={rating}>
                                {rating}
                              </option>
                            ))}
                          </Select>
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </form>
    </AppShell>
  )
}

function cellKey(schoolYear: number, term: number, subject: string) {
  return `${schoolYear}-${term}-${subject}`
}
